"use client"

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useRef,
  useEffect,
} from "react"
import { logger } from "shared-ts/logger"

import { KnownModalRegistry, type ModalId } from "./modal.types"

interface ModalState {
  active: ModalId | null
  // Use as React `key` for guaranteed remount on open
  mountKey: number // monotonic counter
}

interface ModalCtx {
  open(id: ModalId): void
  close(): void
  isOpen(id: ModalId): boolean
  mountKey: number
  trackModalComponent(id: ModalId): () => void
}

const modalCtxLogger = logger.withScope("ModalCtx")

const ModalContext = createContext<ModalCtx | null>(null)

export function ModalContextProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ModalState>({ active: null, mountKey: 0 })
  const modalComponentCounts = useRef<Map<ModalId, number>>(new Map())

  const trackModalComponent = useCallback((id: ModalId) => {
    const map = modalComponentCounts.current

    const instances = (map.get(id) ?? 0) + 1
    map.set(id, instances)

    if (instances > 1) {
      modalCtxLogger.error(
        `<Modal> components (${instances}) detected with duplicate ID: "${id}"`
      )
    }

    return () => {
      const remaining = (map.get(id) ?? 1) - 1
      remaining > 0 ? map.set(id, remaining) : map.delete(id)
    }
  }, [])

  const open = useCallback((id: ModalId) => {
    setState((prev) => {
      if (prev.active !== null) {
        modalCtxLogger.warn(
          `Opening modal ${id} without explicitly closing ${prev.active}`
        )
      }
      return { active: id, mountKey: prev.mountKey + 1 }
    })
  }, [])

  const close = useCallback(() => {
    setState((prev) =>
      prev.active === null ? prev : { ...prev, active: null }
    )
  }, [])

  const isOpen = useCallback(
    (id: ModalId) => state.active === id,
    [state.active]
  )

  const value: ModalCtx = useMemo(
    () => ({
      open,
      close,
      isOpen,
      mountKey: state.mountKey,
      trackModalComponent,
    }),
    [open, close, isOpen, state.mountKey, trackModalComponent]
  )

  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
}

/**
 * Binds modal control to a specific `ModalId`.
 * `mountKey` is reserved for "internal" use by the Modal component.
 */
export function useModal(id: ModalId) {
  const ctx = useContext(ModalContext)
  if (!ctx) throw new Error("useModal must be used within ModalContextProvider")

  const open = useCallback(() => ctx.open(id), [ctx, id])
  const isOpen = useMemo(() => ctx.isOpen(id), [ctx, id])

  return {
    open,
    close: ctx.close,
    isOpen,
    mountKey: ctx.mountKey,
  }
}

export function useModalComponent(id: ModalId): void {
  const ctx = useContext(ModalContext)
  if (!ctx)
    throw new Error(
      "useModalComponent must be used within ModalContextProvider"
    )

  useEffect(() => {
    return ctx.trackModalComponent(id)
  }, [ctx, id])
}

export function useIsAnyKnownModalOpen() {
  const ctx = useContext(ModalContext)
  if (!ctx)
    throw new Error(
      "useIsAnyKnownModalOpen must be used within ModalContextProvider"
    )

  return useMemo(
    () =>
      Object.values(KnownModalRegistry).some((knownModalId) =>
        ctx.isOpen(knownModalId)
      ),
    [ctx]
  )
}
